import React from "react";
import { Image, View, TouchableOpacity } from "react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import { Plus, Trash2, FileText, Activity } from "lucide-react-native";
import { IconInput } from "./IconInput";
import { useMedicalHistoryTheme } from "../theme/MedicalHistoryTheme";
import { useTranslations } from "@src/packages/i18n";
import { DocumentRow } from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { usePressCapture } from "@src/packages/analytics/usePressCapture";

const testResultIllustration =
  require("@src/apps/moon/assets/images/vial-syringe.png") as number;

export type FormFieldValue = {
  id: string;
  testName: string;
  value: string;
  unit: string;
};

export type DynamicFormFieldsProps = {
  fields: FormFieldValue[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, field: Partial<FormFieldValue>) => void;
  unitOptions?: string[];
  testNamePlaceholder?: string;
  valuePlaceholder?: string;
};

/**
 * Dynamic form fields component matching the HTML design.
 * Used for adding/removing multiple form entries (e.g., lab results).
 */
export function DynamicFormFields({
  fields,
  onAdd,
  onRemove,
  onUpdate,
  unitOptions = ["mg/ml", "ng/ml", "pmol/L"],
  testNamePlaceholder,
  valuePlaceholder,
}: DynamicFormFieldsProps) {
  const capturePress = usePressCapture("DynamicFormFields");
  const theme = useMedicalHistoryTheme();
  const t = useTranslations();
  const resolvedTestNamePlaceholder =
    testNamePlaceholder ??
    t("med_history.components.form_fields.test_name_placeholder");
  const resolvedValuePlaceholder =
    valuePlaceholder ??
    t("med_history.components.form_fields.value_placeholder");

  return (
    <View style={{ width: "100%" }}>
      {fields.map((field, index) => (
        <View
          key={field.id}
          style={{
            marginTop: 16,
            gap: 12,
            ...(index < fields.length - 1 && {
              paddingBottom: 24,
              borderBottomWidth: 1,
              borderBottomColor: theme.inputBorder,
            }),
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "baseline",
              justifyContent: "space-between",
            }}
          >
            <Text
              style={{
                fontSize: 12,
                fontWeight: "500",
                color: theme.textMuted,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {t("med_history.components.form_fields.result_n", {
                params: { n: String(index + 1) },
              })}
            </Text>
            {fields.length > 1 && (
              <TouchableOpacity
                onPress={() => {
                  // Trash icon with no visible copy to name it.
                  capturePress("remove_result");
                  onRemove(field.id);
                }}
                style={{ padding: 4 }}
              >
                <Trash2 size={14} color={theme.textFaint} />
              </TouchableOpacity>
            )}
          </View>

          {/* Test Name Input */}
          <View style={{ marginTop: 12 }}>
            <IconInput
              icon={FileText}
              label={resolvedTestNamePlaceholder}
              placeholder={resolvedTestNamePlaceholder}
              value={field.testName}
              onChangeText={(text) => onUpdate(field.id, { testName: text })}
            />
          </View>

          {/* Value and Unit Row */}
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ flex: 1 }}>
              <IconInput
                icon={Activity}
                label={resolvedValuePlaceholder}
                placeholder={resolvedValuePlaceholder}
                value={field.value}
                onChangeText={(text) => onUpdate(field.id, { value: text })}
                keyboardType="numeric"
              />
            </View>
            <View style={{ width: 112 }}>
              <View
                style={{
                  height: 48,
                  backgroundColor: theme.inputBg,
                  borderWidth: 1,
                  borderColor: theme.inputBorder,
                  borderRadius: 12,
                  paddingHorizontal: 12,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text style={{ fontSize: 14, color: theme.text }}>
                  {field.unit || unitOptions[0]}
                </Text>
              </View>
            </View>
          </View>
        </View>
      ))}

      <DocumentRow
        onPress={onAdd}
        accessibilityLabel={t("med_history.components.form_fields.add_another")}
        leading={
          <Image
            source={testResultIllustration}
            resizeMode="contain"
            style={{ width: 34, height: 34 }}
          />
        }
        title={t("med_history.components.form_fields.add_another")}
        trailing={
          <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
        }
        showChevron={false}
        style={{ width: "100%", marginTop: 24 }}
      />
    </View>
  );
}
